// Утилиты дат для форм без @mantine/dates: нативные <input type="date"> дают ISO «yyyy-mm-dd»,
// а бэкенд ждёт «dd.MM.yyyy». Конвертация без часовых поясов (работаем со строкой, не с Date).

/**
 * Сегодняшняя дата ISO «yyyy-mm-dd» по **московскому** времени. Деловые сутки платформы — московские
 * (сервисы работают с `TZ=Europe/Moscow`), поэтому все сравнения «наступил ли день» ведём по ним, а не
 * по часовому поясу браузера: иначе один и тот же момент попал бы у разных людей в разные сутки.
 */
export function todayIsoMoscow(): string {
  // en-CA даёт ровно «yyyy-mm-dd».
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(new Date());
}

/** ISO «yyyy-mm-dd» → «dd.MM.yyyy» (пусто/невалид → null). */
export function isoToRu(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : null;
}

/** «dd.MM.yyyy» → ISO «yyyy-mm-dd» (для предзаполнения нативного input в edit; иначе null). */
export function ruToIso(ru: string | null | undefined): string | null {
  if (!ru) {
    return null;
  }
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(ru.trim());
  if (!match) {
    return null;
  }
  const [, d, m, y] = match;
  return `${y}-${m!.padStart(2, '0')}-${d!.padStart(2, '0')}`;
}

/** Сегодня в ISO (локальная дата, для max декларации). */
export function todayIso(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/** ISO-дата + N дней (для min «вывезти до» = старт + 1). */
export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + days);
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

/** Завтра в ISO (min начала погрузки). */
export function tomorrowIso(): string {
  return addDaysIso(todayIso(), 1);
}

/**
 * Момент времени с сервера (ISO с зоной) → «17.07.2026, 14:32» в часовом поясе читателя.
 *
 * Секунды не показываем: в списке они создают шум, а для разбора есть подробности события.
 * Перевод в местное время обязателен — журнал отвечает на вопрос «когда это было по моим часам».
 *
 * Локаль передаётся, а не берётся жёстко: язык интерфейса переключается, и русский формат даты
 * посреди английского экрана выглядел бы недоделкой.
 */
export function formatDateTime(iso: string | null | undefined, locale = 'ru-RU'): string {
  if (!iso) {
    return '—';
  }
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) {
    return '—';
  }
  return moment.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Разница в днях b−a для ISO-дат (целое; отрицательное, если b раньше a). */
export function diffDaysIso(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  const da = Date.UTC(ay!, (am ?? 1) - 1, ad ?? 1);
  const db = Date.UTC(by!, (bm ?? 1) - 1, bd ?? 1);
  return Math.round((db - da) / 86_400_000);
}
